import { A } from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import Controller, { inject as controller } from '@ember/controller';
import { action, get, set, setProperties } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type { AsyncBelongsTo } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { formatInTimeZone } from 'date-fns-tz';
import type { DetailedChangeset } from 'ember-changeset/types';
import { all, restartableTask, task } from 'ember-concurrency';
import newGroupInviteChangeset from 'garaje/changesets/new-group-invite';
import newInviteChangeset from 'garaje/changesets/new-invite';
import type ConfigModel from 'garaje/models/config';
import type EmployeeModel from 'garaje/models/employee';
import type FlowModel from 'garaje/models/flow';
import GroupInvite from 'garaje/models/group-invite';
import type GroupInviteInstanceTemplate from 'garaje/models/group-invite-instance-template';
import GroupInviteLink from 'garaje/models/group-invite-link';
import type InviteModel from 'garaje/models/invite';
import type InviteTemplate from 'garaje/models/invite-template';
import type LocationModel from 'garaje/models/location';
import RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type ProtectedController from 'garaje/pods/protected/controller';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { shouldClearRecurringForFlow } from 'garaje/utils/clear-recurring-for-ava-flow';
import { DATE_FNS_YYYY_MM_DD } from 'garaje/utils/date-fns-tz-utilities';
import { NON_ASSIGNABLE_FLOWS } from 'garaje/utils/enums';
import { createCapacityValidator } from 'garaje/utils/locations-capacity';
import { isFlowEligibleForLocation, MPB_FEATURE_FLAG } from 'garaje/utils/mpb-location-eligibility';
import parseDateFromQP from 'garaje/utils/parse-date-from-qp';
import type { ModelData } from 'garaje/utils/type-utils';
import addOrUpdateUserData from 'garaje/utils/user-data';
import { filter, reads, sortBy } from 'macro-decorators';
import moment from 'moment-timezone';

import type { VisitorsInvitesIndexRouteModelParams } from '../index/route';

import type { VisitorsInvitesNewRouteModel } from './route';

const PURPOSE_FIELD = 'Purpose of visit';

interface InviteData {
  config?: ConfigModel;
  flows: FlowModel[];
  location: LocationModel;
  priorInvite?: InviteTemplate;
}

export default class VisitorsInvitesNewController extends Controller {
  declare model: VisitorsInvitesNewRouteModel;

  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;

  @controller('protected') protectedController!: ProtectedController;

  queryParams = [
    'type',
    'expectedArrivalTime',
    'name',
    'email',
    'previousInvite',
    'returnTo',
    'returnToModel',
    'reinviteGroupId',
  ];

  @tracked data: InviteData | null = null;
  @tracked expectedArrivalTime = '';
  /**
   * set from QP
   */
  @tracked type: 'single' | 'group' | 'inline' = 'single';
  @tracked returnTo: string | null = null;
  /** Optional dynamic-segment value for the returnTo route (e.g. visitor
   *  profile id when returning to `visitors.profiles.show`). */
  @tracked returnToModel: string | null = null;
  @tracked name = '';
  @tracked email = '';
  @tracked previousInvite = '';
  @tracked reinviteGroupId: string | null = null;

  @reads('state.currentLocation.timezone', 'America/Los_Angeles') timezone!: string;

  get backLink(): { route: string; model?: string } {
    return this.returnTo
      ? { route: this.returnTo, model: this.returnToModel || undefined }
      : { route: 'visitors.invites' };
  }

  @filter(
    'data.flows',
    ({ employeeCentric, type }: FlowModel) => !(<string[]>NON_ASSIGNABLE_FLOWS).includes(type) && !employeeCentric,
  )
  flowOptionsInit!: InviteData['flows'];
  @sortBy('flowOptionsInit', 'position') flowOptions!: InviteData['flows'];

  get locationIsConnectedToProperty(): boolean {
    return this.model.connectedTenants?.slice().length > 0;
  }

  get showLoadingSpinner(): boolean {
    if (this.buildInvite.isRunning || this.setupGroupInviteTask.isRunning) return true;

    return (this.fetchData.isRunning || !this.data) && !this.data?.flows;
  }

  @action
  modelDidSave(
    date?: Date,
    goToIndex = true,
    invite: InviteModel | null = null,
    options: { addAnother?: boolean } = {},
  ): void {
    const { location } = this.data!;

    if (goToIndex) {
      const queryParams: Partial<VisitorsInvitesIndexRouteModelParams> = {};

      if (date) {
        queryParams.date = formatInTimeZone(date, this.timezone, DATE_FNS_YYYY_MM_DD);
      }

      this.router.transitionTo('visitors.invites.index', { queryParams });
      this.maybeSwitchCurrentLocation(location);
    } else {
      // This runs when you "Invite and add another"
      // These attributes are copied to the new invite
      const inviteAttrs = {
        flowName: invite!.flowName,
        expectedArrivalTime: invite!.expectedArrivalTime,
        employee: <AsyncBelongsTo<EmployeeModel>>(<unknown>invite!.employee?.content),
        inviterName: invite!.inviterName,
      };

      this.name = '';
      this.email = '';

      void this.buildInvite.perform(location, inviteAttrs, null, { addAnother: options.addAnother });
    }
    if (this.previousInvite && invite) {
      this.trackInvitesRepeatInviteCreated(this.previousInvite, invite.id);
      this.previousInvite = '';
    }
  }

  @action
  groupInviteDidSave(date?: Date, groupInvite?: GroupInvite | RecurringGroupInvite): void {
    const { location } = this.data!;
    const queryParams: Partial<VisitorsInvitesIndexRouteModelParams> = {};

    let idToNavigateTo: string | undefined;

    if (groupInvite instanceof RecurringGroupInvite) {
      idToNavigateTo = groupInvite.belongsTo('nextGroupInvite').id();
    } else {
      idToNavigateTo = groupInvite?.id;
    }

    if (idToNavigateTo) {
      this.router.transitionTo('visitors.invites.groups.show', idToNavigateTo);
    } else {
      if (date) {
        queryParams.date = formatInTimeZone(date, this.timezone, DATE_FNS_YYYY_MM_DD);
      }

      this.router.transitionTo('visitors.invites.index', { queryParams });
    }

    this.maybeSwitchCurrentLocation(location);
  }

  @action
  dateChanged(date: string): void {
    const now = moment();
    let expectedArrivalTime = moment(date);
    let dateString = expectedArrivalTime.format('YYYY-MM-DDTHH:mm');

    if (expectedArrivalTime.isBefore(now)) {
      expectedArrivalTime = moment(parseDateFromQP(now.format('YYYY-MM-DDTHH:mm')));
      dateString = expectedArrivalTime.format('YYYY-MM-DDTHH:mm');
    }

    if (this.type === 'single') {
      set(this.buildInvite.lastSuccessful!.value!.changeset, 'expectedArrivalTime', expectedArrivalTime.toDate());
    }

    if (this.type === 'group') {
      const changeset = this.buildGroupInviteTask.lastSuccessful!.value!.changeset;

      set(changeset, 'expectedArrivalTime', expectedArrivalTime.toDate());

      if ('groupInviteLinks' in changeset.data) {
        const { groupInviteLinks } = changeset.data;
        groupInviteLinks.forEach((groupInviteLink) => {
          set(groupInviteLink, 'expiresAt', GroupInviteLink.calculateDefaultExpiresAt(expectedArrivalTime.toDate()));
        });
      }
    }

    // update query-param
    this.expectedArrivalTime = dateString;
  }

  @action
  flowChanged(flowName: string, changeset: DetailedChangeset<InviteModel>): void {
    const { location } = this.data!;

    void this.buildInvite.perform(location, { flowName }, changeset);
  }

  @action
  flowChangedForGroupInvite(flow: FlowModel, changeset: DetailedChangeset<GroupInvite>): void {
    const { location } = this.data!;

    void this.buildGroupInviteTask.perform({ location, attrs: { flow }, existingChangeset: changeset });
  }

  @action
  recurringInviteChanged(
    recurringRule: string,
    changeset: DetailedChangeset<GroupInvite | RecurringGroupInvite>,
  ): void {
    const { location } = this.data!;
    void this.buildGroupInviteTask.perform({
      location,
      attrs: {
        recurringRule,
        flow: changeset.data.flow,
      },
      existingChangeset: changeset,
    });
  }

  @action
  modelDidPartialSave({ id: invite_id }: InviteModel): void {
    void this.router.transitionTo('visitors.invites.show', invite_id);
  }

  maybeSwitchCurrentLocation(location: LocationModel): void {
    if (this.model.location !== location) {
      this.protectedController.send('switchLocation', location.id);
    }
  }

  trackInvitesRepeatInviteCreated(previous_invite_id: string, invite_id: string): void {
    const properties = { invite_id, previous_invite_id };
    this.metrics.trackEvent('Invites Repeat Invite Created', properties);
  }

  copyUserDataFromPreviousInvite(invite: InviteModel, previousInvite: InviteTemplate): InviteModel {
    for (const [field, value] of Object.entries(previousInvite.userData)) {
      if (value) addOrUpdateUserData(invite, field, value);
    }

    return invite;
  }

  // restartable so a newer location selection cancels the in-flight fetchData
  // and the stale invite/changeset isn't installed afterwards.
  fetchData = restartableTask(async (location: LocationModel) => {
    const { previousInvite } = this;
    const dataToLoad: [AsyncBelongsTo<ConfigModel>, Promise<FlowModel[]>, undefined | Promise<InviteTemplate>] = [
      location.config,
      this.state.loadFlows({ reload: false, locationId: location.id }),
      undefined,
    ];

    if (previousInvite) {
      // Sideload employee.locations so buildInvite can validate the host
      // against the current location synchronously.
      const include = ['flow', 'employee', 'employee.locations'];
      dataToLoad[2] = this.store.findRecord('invite-template', this.previousInvite, { include, reload: true });
    }

    const [config, flows, priorInvite] = await all(dataToLoad);

    this.data = { config, flows, location, priorInvite };

    await this.buildInvite.perform(location);

    return this.data;
  });

  buildInvite = task(
    async (
      location: LocationModel,
      inviteAttrs: Partial<ModelData<InviteModel>> = {},
      existingChangeset: DetailedChangeset<InviteModel> | null = null,
      options: { addAnother?: boolean } = {},
    ) => {
      let invite: InviteModel;

      const { flowOptions: flows } = this;
      const { priorInvite } = this.data ?? {};

      // Default-flow precedence runs every candidate through the eligibility
      // filter so an "Invite again" or `?flowName=` param can't auto-select a
      // disabled global flow on a non-VIS-ENT primary. Leave the flow unset
      // when every option is ineligible — the picker still lets the user
      // change it but the form won't submit a disabled default.
      const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
      // Build-side gating is intentionally best-effort: no scoped-sub map
      // exists this early, so the helper falls back to the state path, which
      // only resolves a sub for state.currentLocation and fails open for any
      // other primary. The component's reconcile + the map-aware save guard
      // are the authoritative FE gates here; backend remains the final gate.
      const eligibleForPrimary = (flow: FlowModel | null | undefined): boolean =>
        !!flow && isFlowEligibleForLocation(flow, location, isMpbEnabled, this.state);
      const attrFlow = flows?.find((flow) => flow.name == inviteAttrs.flowName);
      // belongsTo('flow').id() reads the relation id without dereferencing an
      // unloaded record; restrict to flows available at the current location
      // so we never auto-select a flow that isn't in the picker. Await the
      // relationship so `flow.isGlobalChild` materializes — the unresolved
      // proxy returns undefined for that field which would silently auto-select
      // an ineligible global flow on a non-capable primary.
      const priorFlowId = priorInvite?.belongsTo('flow').id();
      const priorFlowRel = (
        priorFlowId && flows?.some((f) => f.id === priorFlowId) ? (priorInvite?.flow ?? null) : null
      ) as AsyncBelongsTo<FlowModel> | null;
      const priorFlow = priorFlowRel ? ((await priorFlowRel) as FlowModel | null) : null;
      const firstEligibleFlow = flows?.find((flow) => eligibleForPrimary(flow));
      const flow =
        (eligibleForPrimary(attrFlow) ? attrFlow : undefined) ??
        (eligibleForPrimary(priorFlow) ? priorFlow : undefined) ??
        firstEligibleFlow ??
        (isMpbEnabled ? null : flows?.[0]);
      // eslint-disable-next-line ember/no-get
      const signInFields = <NativeArray<SignInFieldModel>>await get(flow, 'signInFieldPage.signInFields') || A();
      const hostField = signInFields.findBy('identifier', 'host');
      const hostFieldIsRequired = Boolean(hostField?.required);

      // Fun with async code.
      // Sometimes, query params haven't reset yet before the task begins.
      // Load controller state after the await/yield to reduce unintended
      // duplication of name, email.
      const { expectedArrivalTime, previousInvite } = this;
      let { name, email } = this;
      // previousInvite is the QP id; priorInvite is the loaded record.
      const isSamePriorInvite = Boolean(previousInvite && priorInvite?.id === previousInvite);
      const priorFlowApplies = isSamePriorInvite && Boolean(priorFlow);
      const priorHostApplies =
        isSamePriorInvite && Boolean(priorInvite!.employee?.hasMany('locations').ids().includes(location.id));
      const date = expectedArrivalTime ? parseDateFromQP(expectedArrivalTime) : '';

      if (existingChangeset) {
        invite = existingChangeset.data;
      } else {
        let previousHost: EmployeeModel | null = null;
        let previousPrivateNotes: string | undefined;
        let previousPropertyNotes: string | undefined;
        let previousAdditionalHosts: EmployeeModel[] | undefined;
        let previousAdditionalGuests: number | undefined;

        if (isSamePriorInvite) {
          const { privateNotes, propertyNotes, additionalGuests } = priorInvite!;

          if (privateNotes) previousPrivateNotes = privateNotes;
          if (priorInvite!.fullName && !name) name = priorInvite!.fullName;
          if (priorInvite!.email && !email) email = priorInvite!.email;
          if (propertyNotes) previousPropertyNotes = propertyNotes;
          if (additionalGuests) previousAdditionalGuests = additionalGuests;
        }
        if (priorHostApplies && priorInvite!.employee) {
          previousHost = priorInvite!.employee;
        }

        invite = await location.createInvite({
          expectedArrivalTime: date,
          fullName: name,
          email,
          employee: previousHost,
          privateNotes: previousPrivateNotes,
          propertyNotes: previousPropertyNotes,
          additionalHosts: previousAdditionalHosts,
          additionalGuests: previousAdditionalGuests,
        });

        const flowField = invite.userData.findBy('field', PURPOSE_FIELD);

        // There is a "no default flow" feature flag to be aware of
        if (flowField || priorFlowApplies || options.addAnother) {
          // eslint-disable-next-line ember/no-get
          if (flowField) flowField.value = <string>get(flow, 'name');
          // eslint-disable-next-line ember/no-get
          inviteAttrs.flowName = <string>get(flow, 'name');
          // @ts-expect-error
          invite.flow = flow;
        }

        if (priorFlowApplies) this.copyUserDataFromPreviousInvite(invite, priorInvite!);

        // @ts-ignore not the safest way to assign fields to the invite, consider refactoring
        setProperties(invite, inviteAttrs);
      }
      const changeset = newInviteChangeset(invite, {
        hostFieldIsRequired,
        validateCapacity: createCapacityValidator(this.store, location),
        checkEmailRequired:
          this.featureFlags.isEnabled('visitors-required-email-field') ||
          this.locationFeatureFlags.isEnabled('visitors-required-email-field-by-location'),
        store: this.store,
      });

      if (existingChangeset) {
        existingChangeset.changes.forEach((change) => {
          // From Ember Changeset docs:
          // "It is recommended to use changeset.set(...) instead of Ember.set(changeset, ...)"
          // eslint-disable-next-line ember/use-ember-get-and-set
          changeset.set(change['key'], change['value']);
        });
      }

      // The location selector rebuilds this changeset against whichever flow is
      // resolved at the new location. If that flow is AVA-linked, the recurring
      // rule the user set on the previous flow must be dropped — RecurringInvite
      // is not supported for AVA reviews unless the no-reeval flag is on.
      if (shouldClearRecurringForFlow(flow, this.featureFlags)) {
        changeset.recurringRule = null;
        changeset.rruleUntil = null;
      }

      return { changeset, invite, hostFieldIsRequired };
    },
  );

  buildGroupInviteTask = task(
    async ({
      location,
      attrs = {},
      existingChangeset,
      inviteTemplatesToCopy,
    }: {
      location: LocationModel;
      attrs?: Partial<ModelData<GroupInvite & RecurringGroupInvite>>;
      existingChangeset?: DetailedChangeset<GroupInvite | RecurringGroupInvite>;
      inviteTemplatesToCopy?: GroupInviteInstanceTemplate[];
      recurring?: boolean;
    }) => {
      let groupInvite: GroupInvite | RecurringGroupInvite | undefined;
      const { flowOptions: flows } = this;
      const noAutoSelectFlow = this.featureFlags.isEnabled('visitors-no-default-visitor-type') && flows?.length > 1;
      // Run every candidate through the eligibility filter so an explicit
      // `attrs.flow` from a caller (e.g., "Invite again") can't bypass the
      // per-location gating.
      const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
      // Build-side gating is intentionally best-effort: no scoped-sub map
      // exists this early, so the helper falls back to the state path, which
      // only resolves a sub for state.currentLocation and fails open for any
      // other primary. The component's reconcile + the map-aware save guard
      // are the authoritative FE gates here; backend remains the final gate.
      const eligibleForPrimary = (flow: FlowModel | null | undefined): boolean =>
        !!flow && isFlowEligibleForLocation(flow, location, isMpbEnabled, this.state);
      const firstEligibleFlow = flows?.find((flow) => eligibleForPrimary(flow));
      const defaultFlow = isMpbEnabled ? (firstEligibleFlow ?? null) : (firstEligibleFlow ?? (flows && flows[0]));
      const attrFlow = attrs.flow as FlowModel | null | undefined;
      const flow = (eligibleForPrimary(attrFlow) ? attrFlow : null) || (noAutoSelectFlow ? null : defaultFlow);
      const signInFieldPage = await flow?.signInFieldPage;
      const signInFields = await signInFieldPage?.signInFields;

      const hostField = signInFields?.find((field) => field.identifier === 'host');
      const hostFieldIsRequired = Boolean(hostField?.required);
      const { expectedArrivalTime } = this;
      const date = expectedArrivalTime ? parseDateFromQP(expectedArrivalTime) : '';

      if (existingChangeset) {
        // switching type of group invite, recurring vs non-recurring
        if (attrs.recurringRule !== undefined) {
          // cleanup record
          existingChangeset.data.rollbackAttributes();
          groupInvite = location.createGroupInvite({ expectedArrivalTime: date, recurringRule: attrs.recurringRule });
          // `flow` may be null under MPB when no flow on this primary is
          // eligible — leave it unset rather than asserting non-null.
          if (flow) groupInvite.flow = flow;
        } else {
          // other operation like switching flow
          groupInvite = existingChangeset.data;
        }
      } else {
        groupInvite = location.createGroupInvite({ expectedArrivalTime: date });
        // Strip `flow` so setProperties can't re-install a caller-supplied
        // attrs.flow the eligibility filter already rejected; the gated value
        // is assigned last. `flow` may be null under MPB when no flow on this
        // primary is eligible — leave it unset rather than asserting non-null.
        const { flow: _ignoredAttrFlow, ...restAttrs } = attrs;
        // @ts-ignore not the safest way to assign fields to the group invite, consider refactoring
        setProperties(groupInvite, restAttrs);
        if (flow) groupInvite.flow = flow;

        if (this.featureFlags.isEnabled('visitors-generic-invite-link') && groupInvite instanceof GroupInvite) {
          groupInvite.createGroupInviteLink();
        }
      }

      const changeset = newGroupInviteChangeset(groupInvite, {
        hostFieldIsRequired,
        recurring: groupInvite instanceof RecurringGroupInvite,
      });

      if (existingChangeset) {
        existingChangeset.changes.forEach((change) => {
          // From Ember Changeset docs:
          // "It is recommended to use changeset.set(...) instead of Ember.set(changeset, ...)"
          // eslint-disable-next-line ember/use-ember-get-and-set, @typescript-eslint/no-unsafe-argument
          changeset.set(change['key'], change['value']);
        });
      }

      if (groupInvite instanceof RecurringGroupInvite && shouldClearRecurringForFlow(flow, this.featureFlags)) {
        changeset.recurringRule = null;
      }

      return { changeset, groupInvite, hostFieldIsRequired, inviteTemplatesToCopy };
    },
  );

  setupGroupInviteTask = task(async (location: LocationModel) => {
    const groupToCopyPromise = this.reinviteGroupId
      ? this.store.findRecord('group-invite', this.reinviteGroupId, {
          include: `${GroupInvite.groupInviteIncludes()},invite-templates`,
          reload: true,
        })
      : null;

    const dataToLoad = [
      location.config,
      this.state.loadFlows({ reload: false, locationId: location.id }),
      groupToCopyPromise,
    ] as const;
    const [config, flows, groupInviteToCopy] = await all(dataToLoad);
    const currentChangeset = this.buildGroupInviteTask.lastSuccessful?.value?.changeset;

    let attrs: Partial<GroupInvite> = {};

    if (currentChangeset?.isNew || groupInviteToCopy) {
      attrs = {
        groupName: currentChangeset?.groupName ?? groupInviteToCopy?.groupName,
        csv: currentChangeset?.csv,
        notifyVisitor: currentChangeset?.notifyVisitor ?? groupInviteToCopy?.notifyVisitor,
        includeGroupNameInEmail:
          currentChangeset?.includeGroupNameInEmail ?? groupInviteToCopy?.includeGroupNameInEmail,
        privateNotes: currentChangeset?.privateNotes ?? groupInviteToCopy?.privateNotes,
      };

      if (groupInviteToCopy?.flow) {
        attrs.flow = groupInviteToCopy.flow;
      }
    }

    this.data = { config, flows, location };

    await this.buildGroupInviteTask.perform({
      location,
      attrs,
      inviteTemplatesToCopy: groupInviteToCopy?.inviteTemplates.slice(),
    });

    return this.data;
  });
}
